/* STEP 7e-2 · S3 — the engagement's methodology deviations (File assembly page, above the sign-off card): visible to everyone who
   can open the engagement (the team, the reviewer, the appointed EQ reviewer — the server's engagement access rule). The ENGAGEMENT
   PARTNER only acknowledges an open deviation, with an optional note (POST …/methodology-deviations/{id}/acknowledge; the server
   writes the Audit Trail row and refuses anyone else). Shows nothing when the engagement has no deviation (as before 7e-2).
   ``methodologyRefusal`` gives the sign-off card the Arabic text of the server's METHODOLOGY_DEVIATION_UNACKNOWLEDGED refusal. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { STATUS_STYLE } from "@/components/methodology/MethodologyDeviationReport";

export const DEVIATION_EVENT = "methodology-deviations-changed";

/** The server's own text of a methodology refusal in the screen language (null for any other refusal). */
export function methodologyRefusal(err, lang) {
  const d = err?.response?.data || {};
  if (!String(d.error_code || "").startsWith("METHODOLOGY_DEVIATION")) return null;
  return (lang === "ar" && d.detail_ar) || (typeof d.detail === "string" ? d.detail : null);
}

export default function MethodologyDeviationsPanel({ engagementId }) {
  const colW = useResizableColumns({ storageKey: "methodology-deviations-panel", defaults: [240, 160, 240, 130, 240, 160, 110, 110, 160], mins: [130, 100, 130, 90, 130, 100, 72, 72, 100] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    if (!engagementId) return;
    api.get(`/engagements/${engagementId}/methodology-deviations`, { _silent: true }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener(DEVIATION_EVENT, load);
    return () => window.removeEventListener(DEVIATION_EVENT, load);
  }, [load]);
  const rows = asArray(data?.rows);
  if (!rows.length) return null;
  const ack = async (id) => {
    setBusy(id);
    try {
      await api.post(`/engagements/${engagementId}/methodology-deviations/${id}/acknowledge`, { note: notes[id] || "" });
      toast.success(t("s7e2.dev.done"));
      load();
    } catch (e) { toast.error(methodologyRefusal(e, lang) || toErrMsg(e)); }
    finally { setBusy(""); }
  };
  return (
    <Card data-testid="meth-dev-panel">
      <CardHeader>
        <CardTitle className="text-sm">{t("s7e2.dev.title")}</CardTitle>
        <p className="text-xs text-muted-foreground">{t("s7e2.dev.intro")}</p>
        {!data.can_acknowledge && (
          <p className="text-[11px] text-muted-foreground" data-testid="meth-dev-only-partner">
            {data.has_partner ? t("s7e2.dev.onlyPartner") : t("s7e2.dev.noPartner")}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {data.block && (   /* the server's own refusal text: the Partner sign-off and the lock refuse with it until acknowledged */
          <div className="rounded border border-rose-300 bg-rose-50 p-2 text-[12px] text-rose-900 dark:bg-rose-950/30 dark:text-rose-100"
               role="alert" data-testid="meth-dev-block">{(lang === "ar" && data.block.detail_ar) || data.block.detail}</div>
        )}
        <div className="overflow-auto rounded border">
          <ResizableTable cols={colW} className="w-full text-[12px]" data-testid="meth-dev-panel-table">
            <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-900">
              <tr>
                <ResizableTh index={0} className="px-2 py-1 text-start">{t("s7e2.dev.colItem")}</ResizableTh>
                <ResizableTh index={1} className="px-2 py-1 text-start">{t("s7e2.dev.colSetting")}</ResizableTh>
                <ResizableTh index={2} className="px-2 py-1 text-start">{t("s7e2.dev.colFirm")}</ResizableTh>
                <ResizableTh index={3} className="px-2 py-1 text-start">{t("s7e2.dev.colValue")}</ResizableTh>
                <ResizableTh index={4} className="px-2 py-1 text-start">{t("s7e2.dev.colReason")}</ResizableTh>
                <ResizableTh index={5} className="px-2 py-1 text-start">{t("s7e2.dev.colBy")}</ResizableTh>
                <ResizableTh index={6} className="px-2 py-1 text-start">{t("s7e2.dev.colVersion")}</ResizableTh>
                <ResizableTh index={7} className="px-2 py-1 text-start">{t("s7e2.dev.colStatus")}</ResizableTh>
                <ResizableTh index={8} className="px-2 py-1 text-start">{t("s7e2.dev.colAck")}</ResizableTh>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.id} className="align-top" data-testid={`meth-dev-row-${r.id}`}>
                  <td className="px-2 py-1">{t(`s7e2.dev.area.${r.area}`)}{r.area === "sampling" && r.subject_label ? ` — ${r.subject_label}` : ""}</td>
                  <td className="px-2 py-1">{lang === "ar" ? r.setting_label_ar : r.setting_label}</td>
                  <td className="px-2 py-1 font-mono">{lang === "ar" ? r.firm_text_ar : r.firm_text}</td>
                  <td className="px-2 py-1 font-mono">{r.engagement_value} %</td>
                  <td className="px-2 py-1">{r.reason}</td>
                  <td className="px-2 py-1">{r.by}<div className="font-mono text-[10px]">{fmtDateTime(r.at)}</div></td>
                  <td className="px-2 py-1 font-mono">V{r.methodology_version}</td>
                  <td className="px-2 py-1">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${STATUS_STYLE[r.display_status] || ""}`} data-testid={`meth-dev-status-${r.id}`}>
                      {t(`s7e2.dev.status.${r.display_status}`)}
                    </span>
                    {r.status === "closed" && <div className="text-[10px] text-muted-foreground">{t("s7e2.dev.closedAt").replace("{at}", fmtDateTime(r.closed_at)).replace("{v}", `${r.closed_value} %`)}</div>}
                  </td>
                  <td className="px-2 py-1">
                    {r.acknowledged_by ? (
                      <div className="text-[11px]">{t("s7e2.dev.acked").replace("{by}", r.acknowledged_by).replace("{at}", fmtDateTime(r.acknowledged_at))}
                        {r.ack_note ? <div className="text-muted-foreground">{r.ack_note}</div> : null}</div>
                    ) : (r.display_status === "open" && data.can_acknowledge) ? (
                      <div className="flex items-center gap-1">
                        <Input className="h-7 w-44 text-[11px]" placeholder={t("s7e2.dev.notePh")} value={notes[r.id] || ""}
                               onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} data-testid={`meth-dev-note-${r.id}`} />
                        <Button size="sm" className="h-7 text-[11px]" disabled={busy === r.id} onClick={() => ack(r.id)} data-testid={`meth-dev-ack-${r.id}`}>
                          {t("s7e2.dev.ack")}
                        </Button>
                      </div>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </div>
      </CardContent>
    </Card>
  );
}
